import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes, { any } from 'prop-types';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { CheckBox } from '../../../components/Form';

import { rescheduleMatch, scheduleMatch, toggleSelectAllMatchesOnCourt } from '../../../actions/court-scheduler-actions';
import Match from './Match';
import MatchTimeConflictsPopup from '../../../components/PopUps/MatchTimeConflictsPopup';
import { clampCalendarMaxToDayEnd, getMatchEventFromScheduledMatch, snapDownToNearestIncrement } from '../../../utils/courtSchedular';
import { TIME_FORMAT_12H } from '../../../utils/formatting';

import styles from './courtSchedular.scss';

const DnDCalendar = withDragAndDrop(Calendar);

const CourtGridCalendar = (props) => {
    const {
        className,
        event: eventData,
        onEditMatchSettings,
    } = props;

    const { eventSchedule } = eventData;

    const dispatch = useDispatch();
    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);
    const {
        selectedDate,
        selectedDateCourtConfig,
        courtsOfSelectedFacility,
        scheduledMatches = [],
        showTimeConflictsError,
        selectedDrawWithMatches,
        selectedMatchIds = [],
    } = courtScheduler;

    const localizer = useMemo(() => {
        if (eventSchedule) {
            moment.tz.setDefault(eventSchedule.timeZone?.value);
            return momentLocalizer(moment);
        }
    }, [eventSchedule]);

    useEffect(() => {
        return () => {
            moment.tz.setDefault();
        };
    }, []);

    const resizableAccessor = useCallback(() => {
        return false;
    }, []);

    const isMatchNotInSelectedDraw = useCallback((match) => {
        return selectedDrawWithMatches &&
            (selectedDrawWithMatches !== match.drawId && selectedDrawWithMatches !== match.parentDrawId);
    }, [selectedDrawWithMatches]);

    const draggableAccessor = useCallback((event) => {
        return !event?.data?.disableDragActions;
    }, []);

    const minCalendarDate = useMemo(() => {
        const sortedScheduleMatches = scheduledMatches
            .sort((match1, match2) => {
                const match1StartTime = moment.utc(match1.startTime).tz(eventSchedule?.timeZone?.value);
                const match2StartTime = moment.utc(match2.startTime).tz(eventSchedule?.timeZone?.value);

                if (match1StartTime.isSame(match2StartTime)) {
                    return 0;
                }

                if (match1StartTime.isBefore(match2StartTime)) {
                    return -1;
                }

                return 1;
            });

        const startTimeAsPerConfig = moment.utc(selectedDateCourtConfig.startTime).tz(eventSchedule?.timeZone?.value);

        if (sortedScheduleMatches.length > 0) {
            const firstMatchOfDayStartTime = moment.utc(sortedScheduleMatches[0].startTime).tz(eventSchedule?.timeZone?.value);
            if (firstMatchOfDayStartTime.isBefore(startTimeAsPerConfig)) {
                if (firstMatchOfDayStartTime.isBefore(startTimeAsPerConfig.startOf('day'))) {
                    return startTimeAsPerConfig.startOf('day').toDate();
                }

                return firstMatchOfDayStartTime.toDate();
            }
        }

        return startTimeAsPerConfig.toDate();
    }, [selectedDateCourtConfig, eventSchedule, scheduledMatches]);

    const maxCalendarDate = useMemo(() => {
        const sortedScheduleMatches = scheduledMatches
            .sort((match1, match2) => {
                const match1EndTime = moment.utc(match1.endTime).tz(eventSchedule?.timeZone?.value);
                const match2EndTime = moment.utc(match2.endTime).tz(eventSchedule?.timeZone?.value);

                if (match1EndTime.isSame(match2EndTime)) {
                    return 0;
                }

                if (match1EndTime.isBefore(match2EndTime)) {
                    return 1;
                }

                return -1;
            });

        const endTimeAsPerConfig = moment.utc(selectedDateCourtConfig.endTime).tz(eventSchedule?.timeZone?.value);

        if (sortedScheduleMatches.length > 0) {
            const lastmatchOfDayEndTime = moment.utc(sortedScheduleMatches[0].endTime).tz(eventSchedule?.timeZone?.value);
            if (lastmatchOfDayEndTime.isAfter(endTimeAsPerConfig)) {
                if (lastmatchOfDayEndTime.isAfter(endTimeAsPerConfig.endOf('day'))) {
                    return endTimeAsPerConfig.endOf('day').toDate();
                }

                return lastmatchOfDayEndTime.toDate();
            }
        }

        return endTimeAsPerConfig.toDate();
    }, [selectedDateCourtConfig, eventSchedule, scheduledMatches]);

    const clampedMaxCalendarDate = useMemo(() => {
        return clampCalendarMaxToDayEnd(minCalendarDate, maxCalendarDate, eventSchedule?.timeZone?.value);
    }, [minCalendarDate, maxCalendarDate, eventSchedule?.timeZone?.value]);

    const dateOfConfig = useMemo(() => {
        return moment.utc(selectedDate).tz(eventSchedule?.timeZone?.value).toDate();
    }, [selectedDateCourtConfig, eventSchedule]);

    const timeFormats = useMemo(() => ({
        timeGutterFormat: (date) => moment(date).format(TIME_FORMAT_12H),
    }), []);

    const onChangeEventTime = useCallback((eventChangeObject) => {
        const { start, end, resourceId, event } = eventChangeObject;
        const match = event.data;
        const isCourtChanged = match.courtId !== resourceId;

        const newStartTime = moment.utc(start).tz(eventSchedule?.timeZone?.value);
        const newEndTime = moment.utc(end).tz(eventSchedule?.timeZone?.value);
        const isStartTimeChanged = !moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).isSame(newStartTime);
        const isEndTimeChanged = !moment.utc(match.endTime).tz(eventSchedule?.timeZone?.value).isSame(newEndTime);

        if (isCourtChanged || isStartTimeChanged || isEndTimeChanged) {
            dispatch(rescheduleMatch(start, end, resourceId, event.data));
        }
    }, []);

    const onDroppedFromOutside = useCallback((dropData) => {
        const { start, resource } = dropData;
        dispatch(scheduleMatch(start, resource));
    }, []);

    const onSelectEvent = useCallback((event) => {
        if (!event.data.addDisableOverlay) {
            onEditMatchSettings(event.data);
        }
    }, [onEditMatchSettings]);

    const renderEventComponent = useCallback(({ event }) => {
        if (event?.data) {
            return (
                <Match
                    match={event?.data}
                    isScheduled
                />
            );
        }
        return null;
    }, []);

    const renderResourceHeader = useCallback(({ resource }) => {
        const courtMatches = scheduledMatches.filter((m) => m.courtId === resource.id);
        const isAllSelected = courtMatches.length > 0 && courtMatches.every((m) => selectedMatchIds.includes(m.id));

        const onCheckboxChange = (checked, ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            dispatch(toggleSelectAllMatchesOnCourt(resource.id));
        };

        return (
            <div className={styles.courtContainer}>
                <div className="flex-1">{resource.name}</div>
                <CheckBox
                    id={`select-${resource.id}`}
                    formGroupCssClass={`mb0 ${styles.courtSelect} ${isAllSelected && styles.selected}`}
                    controlled={true}
                    checked={isAllSelected}
                    onChange={onCheckboxChange}
                    disabled={false}
                    data-testid="court-scheduler.select-item-checkbox"
                />
            </div>
        );
    }, [scheduledMatches, selectedMatchIds, dispatch]);

    const components = useMemo(() => ({
        event: renderEventComponent,
        resourceHeader: renderResourceHeader,
    }), [renderEventComponent, renderResourceHeader]);

    const calendarEvents = useMemo(() => {
        return scheduledMatches
            .map((match) => ({
                ...match,
                disableDragActions: isMatchNotInSelectedDraw(match),
                addDisableOverlay: isMatchNotInSelectedDraw(match),
            }))
            .map((match) => getMatchEventFromScheduledMatch(match, eventSchedule));
    }, [scheduledMatches, isMatchNotInSelectedDraw]);

    if (!localizer) {
        return null;
    }

    return (
        <div className={`${styles.courtGridCalendar} ${className}`}>
            <DnDCalendar
                defaultDate={dateOfConfig}
                defaultView={Views.DAY}
                events={calendarEvents}
                localizer={localizer}
                resourceIdAccessor="id"
                resources={courtsOfSelectedFacility}
                resourceTitleAccessor="name"
                dayLayoutAlgorithm="no-overlap"
                step={snapDownToNearestIncrement(selectedDateCourtConfig.timeIncrements)}
                timeslots={1}
                views={[Views.DAY]}
                toolbar={false}
                min={minCalendarDate}
                max={clampedMaxCalendarDate}
                onEventDrop={onChangeEventTime}
                onDropFromOutside={onDroppedFromOutside}
                draggableAccessor={draggableAccessor}
                resizableAccessor={resizableAccessor}
                components={components}
                onSelectEvent={onSelectEvent}
                formats={timeFormats}
            />
            {showTimeConflictsError && (
                <MatchTimeConflictsPopup />
            )}
        </div>
    );
};

CourtGridCalendar.propTypes = {
    className: PropTypes.string,
    event: PropTypes.objectOf(any).isRequired,
    onEditMatchSettings: PropTypes.func.isRequired,
};

CourtGridCalendar.defaultProps = {
    className: '',
};

export default CourtGridCalendar;
